import { ButtonGraphicsDecorationType } from '../Model/StyleModel.js';
import { assertNever } from '../Util.js';
import { ButtonDecorationRenderer } from './ButtonDecorationRenderer.js';
import { buildSelectionMarker, computeSelectionMarkerLines } from './Geometry.js';
import { DrawBounds, parseColor, parseColorAlpha, rgbRev } from './Util.js';
/**
 * Text outline width as a fraction of the font size. Proportional (rather than a fixed pixel value) so
 * the outline keeps a consistent visual weight relative to its text at any button/canvas size. Because
 * it derives from the render-size font size, it is automatically resolution-independent.
 */
const TEXT_OUTLINE_FACTOR = 1 / 16;
export class GraphicsLayeredButtonRenderer {
    static async draw(img, drawStyle, elementsToHide, selectedElementId, paddingPx) {
        const backgroundElement = drawStyle.elements[0]?.type === 'canvas' ? drawStyle.elements[0] : undefined;
        const drawWidth = img.width - paddingPx.x * 2;
        const drawHeight = img.height - paddingPx.y * 2;
        // Read the resolved `decoration`, not the raw one off the canvas
        const decoration = drawStyle.decoration;
        const showTopBar = decoration === ButtonGraphicsDecorationType.TopBar;
        const topBarBounds = new DrawBounds(paddingPx.x, paddingPx.y, drawWidth, Math.max(ButtonDecorationRenderer.DEFAULT_HEIGHT, Math.floor(0.2 * drawHeight)));
        const topBarHeight = showTopBar ? topBarBounds.height : 0;
        const drawBounds = new DrawBounds(paddingPx.x, paddingPx.y + topBarHeight, drawWidth, drawHeight - topBarHeight);
        this.#drawBackgroundElement(img, drawBounds, backgroundElement);
        // Clip element drawing to the button rectangle, so that only the markers draw outside the bounds
        const clipBounds = paddingPx.x > 0 || paddingPx.y > 0 ? new DrawBounds(paddingPx.x, paddingPx.y, drawWidth, drawHeight) : null;
        const selectedMarker = await img.usingClip(clipBounds, async () => this.#drawElements(img, drawStyle.elements, elementsToHide, selectedElementId, drawBounds, false));
        switch (decoration) {
            case ButtonGraphicsDecorationType.None:
                // Do nothing
                break;
            case ButtonGraphicsDecorationType.Border:
                ButtonDecorationRenderer.drawBorderWhenPushed(img, drawStyle, drawBounds);
                break;
            case ButtonGraphicsDecorationType.TopBar:
                // Clip to the bar so a long location label cannot overflow into the padding around the button
                await img.usingClip(topBarBounds, async () => {
                    ButtonDecorationRenderer.drawStatusBar(img, drawStyle, topBarBounds, false);
                });
                break;
            default:
                assertNever(decoration);
                break;
        }
        // Draw top status icons
        if (drawStyle.show_status_icons) {
            ButtonDecorationRenderer.drawIcons(img, drawStyle, topBarBounds);
        }
        // Draw a border around the selected element, do this last so it's on top
        if (selectedMarker)
            this.#drawBoundsLines(img, selectedMarker);
    }
    /**
     * Draw the elements to the image
     * Returns the selected element bounds, or null if no element was selected or the selected element was not found
     */
    static async #drawElements(img, elements, elementsToHide, selectedElementId, drawBounds, skipDrawParent) {
        let selectedMarker = null;
        for (const element of elements) {
            // Skip the background element, it's handled separately
            if (element.type === 'canvas')
                continue;
            const skipDraw = skipDrawParent || elementsToHide.has(element.id) || !element.enabled;
            let elementBounds = null;
            try {
                switch (element.type) {
                    case 'group': {
                        // Compute the group's own bounds first so rotation pivots about its centre, not the container's
                        let groupBounds = drawBounds.compose(element.x, element.y, element.width, element.height);
                        elementBounds = groupBounds; // Capture the pre-square bounds
                        if (element.squareCoords) {
                            const squareSize = Math.min(groupBounds.width, groupBounds.height);
                            groupBounds = new DrawBounds(groupBounds.x + (groupBounds.width - squareSize) / 2, groupBounds.y + (groupBounds.height - squareSize) / 2, squareSize, squareSize);
                        }
                        await img.usingTemporaryLayer(element.opacity, async (img) => {
                            await img.usingRotation(groupBounds, element.rotation, async () => {
                                // Propagate the selected child, prefixing this group's rotation so the marker
                                // is drawn in the same rotated frame the child was drawn in
                                const childMarker = await this.#drawElements(img, element.children, elementsToHide, selectedElementId, groupBounds, skipDraw);
                                if (childMarker) {
                                    selectedMarker = buildSelectionMarker(childMarker.bounds, groupBounds, element.rotation, childMarker.rotations);
                                }
                            });
                        });
                        break;
                    }
                    case 'reference': {
                        // Compute the reference's own bounds first so rotation pivots about its centre, not the container's
                        const referenceBounds = drawBounds.compose(element.x, element.y, element.width, element.height);
                        elementBounds = referenceBounds;
                        await img.usingTemporaryLayer(element.opacity, async (img) => {
                            await img.usingRotation(referenceBounds, element.rotation, async () => {
                                // Note: children of a reference element cannot be individually selected,
                                // so the return value (selected child bounds) is intentionally discarded.
                                await this.#drawElements(img, element.children, elementsToHide, selectedElementId, referenceBounds, skipDraw);
                            });
                        });
                        break;
                    }
                    case 'image':
                        elementBounds = await this.#drawImageElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'text':
                        elementBounds = await this.#drawTextElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'box':
                        elementBounds = await this.#drawBoxElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'line':
                        elementBounds = await this.#drawLineElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'circle':
                        elementBounds = await this.#drawCircleElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'gauge':
                        elementBounds = await this.#drawGaugeElement(img, drawBounds, element, skipDraw);
                        break;
                    default:
                        assertNever(element);
                }
            }
            catch (_e) {
                // TODO - log/report error where? Or should this abandon the render and do a placeholder?
            }
            // Capture the selected element's bounds and rotation, to draw the marker later
            if (element.id === selectedElementId && elementBounds) {
                const rotation = 'rotation' in element ? element.rotation : 0;
                selectedMarker = buildSelectionMarker(elementBounds, elementBounds, rotation);
            }
        }
        return selectedMarker;
    }
    static #drawBackgroundElement(_img, _drawBounds, backgroundElement) {
        if (!backgroundElement)
            return;
        // img.box(drawBounds.x, drawBounds.y, drawBounds.maxX, drawBounds.maxY, parseColor(backgroundElement.color))
    }
    static async #drawImageElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw || !element.base64Image)
            return drawBounds;
        let imageDrawn = null;
        try {
            const imageData = element.base64Image;
            await img.usingAlpha(element.opacity, async () => {
                await img.usingRotation(drawBounds, element.rotation, async () => {
                    imageDrawn = await img.drawBase64Image(imageData, drawBounds.x, drawBounds.y, drawBounds.width, drawBounds.height, element.halign, element.valign, element.fillMode);
                });
            });
        }
        catch (e) {
            console.error('error drawing image:', e);
        }
        if (imageDrawn === false) {
            await img.usingTemporaryLayer(element.opacity, async (img) => {
                await img.usingRotation(drawBounds, element.rotation, async () => {
                    const { x, y, width, height, maxX, maxY } = drawBounds;
                    // Orange background
                    img.box(x, y, maxX, maxY, '#ff8c00');
                    // Square warning triangle icon (same style as the status bar warning icon)
                    const iconSize = Math.round(Math.min(width * 0.5, height * 0.33));
                    const iconCenterX = x + width / 2;
                    const iconTop = y + height * 0.1;
                    img.drawFilledPath([
                        [iconCenterX - iconSize / 2, iconTop + iconSize], // bottom-left
                        [iconCenterX + iconSize / 2, iconTop + iconSize], // bottom-right
                        [iconCenterX, iconTop], // apex
                    ], '#ffffff');
                    // Bold "!" inside the triangle, matching the status bar icon
                    img.drawTextLineAligned(iconCenterX, iconTop + iconSize, '!', '#ff8c00', Math.floor(iconSize * 0.7), 'center', 'bottom', 'bold');
                    // "image error" label immediately below the icon
                    const textY = iconTop + iconSize + Math.round(height * 0.04);
                    img.drawAlignedText(x, textY, width, maxY - textY, 'image error', '#ffffff', maxY - textY, true, 'center', 'center');
                });
            });
        }
        return drawBounds;
    }
    static async #drawTextElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw || !element.text)
            return drawBounds;
        // Draw button text
        // Scale font to be a percentage relative to the height of the draw area
        const fontSize = (element.fontsize * drawBounds.height) / 100 / 1.2;
        // Force some padding around the text, scaled proportionally
        const marginScale = 0.015;
        const marginX = 2 * marginScale * drawBounds.width;
        const marginY = 1 * marginScale * drawBounds.height;
        await img.usingTemporaryLayer(element.opacity, async (img) => {
            await img.usingRotation(drawBounds, element.rotation, async () => {
                img.drawAlignedText(drawBounds.x + marginX, drawBounds.y + marginY, drawBounds.width - 2 * marginX, drawBounds.height - 2 * marginY, element.text, parseColor(element.color), fontSize, element.fontsizeAllowShrink, element.halign, element.valign, parseColorAlpha(element.outlineColor) > 0
                    ? {
                        width: fontSize * TEXT_OUTLINE_FACTOR,
                        color: parseColor(element.outlineColor),
                    }
                    : undefined, element.font);
            });
        });
        return drawBounds;
    }
    static async #drawBoxElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw)
            return drawBounds;
        // Calculate a pixel width, relative to the parent bounds
        const borderWidth = Math.max(0, parentBounds.width, parentBounds.height) * element.borderWidth;
        await img.usingTemporaryLayer(element.opacity, async (img) => {
            await img.usingRotation(drawBounds, element.rotation, async () => {
                img.box(drawBounds.x, drawBounds.y, drawBounds.maxX, drawBounds.maxY, parseColor(element.color), {
                    color: parseColor(element.borderColor),
                    width: borderWidth,
                }, element.borderPosition);
            });
        });
        return drawBounds;
    }
    static async #drawLineElement(img, parentBounds, element, skipDraw) {
        // Convert from fractional coordinates (0-1) to pixel coordinates within parent bounds
        const fromX = parentBounds.x + element.fromX * parentBounds.width;
        const fromY = parentBounds.y + element.fromY * parentBounds.height;
        const toX = parentBounds.x + element.toX * parentBounds.width;
        const toY = parentBounds.y + element.toY * parentBounds.height;
        // Calculate bounds for selection (use the bounding box of the line)
        const minX = Math.min(fromX, toX);
        const minY = Math.min(fromY, toY);
        const maxX = Math.max(fromX, toX);
        const maxY = Math.max(fromY, toY);
        const drawBounds = new DrawBounds(minX, minY, maxX - minX, maxY - minY);
        if (skipDraw)
            return drawBounds;
        // A zero width hides the line; the 1px floor below is only to keep thin (but non-zero) lines visible
        if (element.borderWidth <= 0)
            return drawBounds;
        // Calculate a pixel width, relative to the parent bounds
        const borderWidth = Math.max(1, Math.max(parentBounds.width, parentBounds.height) * element.borderWidth);
        // The stroke is centred on the path; shift it perpendicular by half its width to sit left/right of it
        let ox = 0;
        let oy = 0;
        if (element.borderPosition !== 'center') {
            const length = Math.hypot(toX - fromX, toY - fromY);
            if (length > 0) {
                // Unit vector to the right of travel is (-dy, dx); left is the negation
                const sign = element.borderPosition === 'right' ? 1 : -1;
                ox = (sign * -(toY - fromY) * borderWidth) / (2 * length);
                oy = (sign * (toX - fromX) * borderWidth) / (2 * length);
            }
        }
        await img.usingAlpha(element.opacity, async () => {
            img.line(fromX + ox, fromY + oy, toX + ox, toY + oy, {
                color: parseColor(element.borderColor),
                width: borderWidth,
            });
        });
        return drawBounds;
    }
    static async #drawCircleElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw)
            return drawBounds;
        // Calculate a pixel width, relative to the parent bounds
        const borderWidth = Math.max(0, parentBounds.width, parentBounds.height) * element.borderWidth;
        await img.usingTemporaryLayer(element.opacity, async (img) => {
            const midX = drawBounds.x + drawBounds.width / 2;
            const midY = drawBounds.y + drawBounds.height / 2;
            const radiusX = drawBounds.width / 2;
            const radiusY = drawBounds.height / 2;
            const startAngle = this.#angleToRadians(element.startAngle + 90);
            const endAngle = this.#angleToRadians(element.endAngle + 90);
            img.circle(midX, midY, radiusX, radiusY, startAngle, endAngle, element.drawSlice, parseColor(element.color), {
                color: parseColor(element.borderColor),
                width: borderWidth,
            }, element.borderOnlyArc, element.borderPosition);
        });
        return drawBounds;
    }
    static async #drawGaugeElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw)
            return drawBounds;
        const { x, y, width, height, maxX, maxY } = drawBounds;
        const { orientation, reverse, multiColour, trackStyle, symmetric } = element;
        const finite = (v, fallback) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : fallback;
        };
        // --- Value mapping (authored Min..Max domain → 0–100 track position) ---
        const min = finite(element.min, 0);
        const max = finite(element.max, 100);
        const range = max - min;
        const norm = (v) => {
            if (range === 0)
                return 0;
            return Math.max(0, Math.min(100, ((v - min) / range) * 100));
        };
        const valuePos = norm(finite(element.value, 0));
        // `origin === null` is the "auto" default: a normal bar grows from the minimum (track position 0).
        const originPos = norm(element.origin === null ? min : finite(element.origin, min));
        // Active fill interval in track-position space (0–100).
        // Non-symmetric: from the origin toward the value (handles normal bars, centre-bar, pan).
        // Symmetric: a band of length = value, centred on the origin (handles stereo-width).
        let fillLo;
        let fillHi;
        if (symmetric) {
            const half = valuePos / 2;
            fillLo = Math.max(0, Math.min(100, originPos - half));
            fillHi = Math.max(0, Math.min(100, originPos + half));
        }
        else {
            fillLo = Math.min(originPos, valuePos);
            fillHi = Math.max(originPos, valuePos);
        }
        const hasFill = element.fillEnabled && fillHi > fillLo;
        const trackWidth = Math.max(0, Math.min(100, finite(element.trackWidth, 100))) / 100;
        const trackAmount = Math.max(0, Math.min(100, finite(element.trackAmount, 0))) / 100;
        // --- Color stops → runs between consecutive stops, with the first anchored to position 0
        //     and the last extended to 100 so the track never has an uncolored gap. ---
        const stops = [...element.stops]
            .map((s) => ({ pos: norm(finite(s.value, 0)), color: finite(s.color, 0), gradient: !!s.gradient }))
            .sort((a, b) => a.pos - b.pos);
        if (stops.length === 0)
            return drawBounds;
        const runs = [];
        for (let i = 0; i < stops.length; i++) {
            const start = i === 0 ? 0 : stops[i].pos;
            const end = i + 1 < stops.length ? stops[i + 1].pos : 100;
            if (end <= start)
                continue;
            const gradient = stops[i].gradient && i + 1 < stops.length;
            runs.push({
                start,
                end,
                colorStart: stops[i].color,
                colorEnd: gradient ? stops[i + 1].color : stops[i].color,
                gradient,
            });
        }
        if (runs.length === 0)
            return drawBounds;
        // Single-color fill: color of the highest stop whose position <= value.
        let singleColor = stops[0].color;
        for (const s of stops)
            if (s.pos <= valuePos)
                singleColor = s.color;
        const lerp = (a, b, t) => a + (b - a) * t;
        const rgbaAt = (run, p) => {
            const c0 = rgbRev(run.colorStart, true);
            if (!run.gradient)
                return c0;
            const c1 = rgbRev(run.colorEnd, true);
            const span = run.end - run.start;
            const t = span > 0 ? Math.max(0, Math.min(1, (p - run.start) / span)) : 0;
            return { r: lerp(c0.r, c1.r, t), g: lerp(c0.g, c1.g, t), b: lerp(c0.b, c1.b, t), a: lerp(c0.a, c1.a, t) };
        };
        const cssOf = (c) => `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${c.a})`;
        // Track (unfilled) color. 'transparent' base colors are emitted at full alpha and composited
        // through a temporary layer at trackAmount; 'dimmed' darkens the color in place.
        const trackTransform = (c) => {
            if (trackStyle === 'transparent')
                return c;
            return { r: c.r * trackAmount, g: c.g * trackAmount, b: c.b * trackAmount, a: c.a };
        };
        // --- Geometry helpers shared by fill, track and marker passes. ---
        const isRing = orientation === 'ring';
        const isHorizontal = orientation === 'horizontal';
        // Cross-axis geometry. The fill (indicator) uses the FULL cross-axis; trackWidth only
        // narrows the unfilled track, so the fill can be drawn wider than the track.
        const crossFull = isHorizontal ? height : width;
        const fillHalf = crossFull / 2;
        const trackHalf = (crossFull * trackWidth) / 2;
        const bandCenter = isHorizontal ? y + height / 2 : x + width / 2;
        const markerW = Math.max(1, Math.min(100, finite(element.markerWidth, 15))) / 100;
        // The marker is centred on the value, so at value min/max half of it would overhang the ends and make
        // the gauge look bigger than the same gauge at another value. Inset the value's travel by half a marker
        // at each end so the marker stays inside; the runs at the very ends still extend to the true edge (via
        // `mapX/mapY/mapAngle` below) so the track fills the whole gauge with whatever colour is there.
        const travelLen = isHorizontal ? width : height;
        const linearInset = element.markerEnabled ? Math.min(Math.max(1, crossFull * markerW) / 2, travelLen / 2) : 0;
        // `*Full` maps to the true ends (p=0 → start edge, p=100 → end edge); the plain maps inset the value's
        // travel by the marker half-width. `atEdge` picks the true edge for run ends that sit at 0/100.
        const atEdge = (p) => p <= 1e-6 || p >= 100 - 1e-6;
        const posToXFull = (p) => (reverse ? maxX - (p / 100) * width : x + (p / 100) * width);
        const posToYFull = (p) => (reverse ? y + (p / 100) * height : maxY - (p / 100) * height);
        const posToX = (p) => reverse
            ? maxX - linearInset - (p / 100) * (width - 2 * linearInset)
            : x + linearInset + (p / 100) * (width - 2 * linearInset);
        const posToY = (p) => reverse
            ? y + linearInset + (p / 100) * (height - 2 * linearInset)
            : maxY - linearInset - (p / 100) * (height - 2 * linearInset);
        const mapX = (p) => (atEdge(p) ? posToXFull(p) : posToX(p));
        const mapY = (p) => (atEdge(p) ? posToYFull(p) : posToY(p));
        // Ring geometry.
        const cx = x + width / 2;
        const cy = y + height / 2;
        const outerRadius = Math.min(width, height) / 2;
        const ringWidthPx = outerRadius * (element.ringWidth / 100);
        const arcRadius = outerRadius - ringWidthPx / 2;
        // Ring stroke widths: fill uses the full ring width; the track is narrowed by trackWidth.
        const fillStrokePx = ringWidthPx;
        const trackStrokePx = ringWidthPx * trackWidth;
        // Arc span: clockwise from startAngle to endAngle (degrees, 0 = top). 0 span → full circle.
        const startAngleDeg = finite(element.startAngle, 0);
        const endAngleDeg = finite(element.endAngle, 360);
        let sweepDeg = (((endAngleDeg - startAngleDeg) % 360) + 360) % 360;
        if (sweepDeg === 0)
            sweepDeg = 360;
        const sweepRad = (sweepDeg * Math.PI) / 180;
        const degToRad = (deg) => -Math.PI / 2 + (deg * Math.PI) / 180;
        // Inset the ring's angular travel by the marker bead's half-angle, same reasoning as the linear inset.
        const ringInset = element.markerEnabled
            ? Math.min(Math.max(1, ringWidthPx * markerW) / 2 / arcRadius, sweepRad / 2)
            : 0;
        const ringInsetFrac = sweepRad > 0 ? ringInset / sweepRad : 0;
        // p=0 at startAngle, p=100 at endAngle (clockwise). reverse flips which end is p=0.
        const posToAngleFull = (p) => degToRad(startAngleDeg + (reverse ? 1 - p / 100 : p / 100) * sweepDeg);
        const posToAngle = (p) => {
            const t = (reverse ? 1 - p / 100 : p / 100) * (1 - 2 * ringInsetFrac) + ringInsetFrac;
            return degToRad(startAngleDeg + t * sweepDeg);
        };
        const mapAngle = (p) => (atEdge(p) ? posToAngleFull(p) : posToAngle(p));
        // Paint a single position-space interval [a, b] with one solid color onto `target`.
        // `wide` selects the fill width (full) vs the narrowed track width.
        const paintSolid = (target, a, b, color, wide) => {
            if (b - a <= 1e-6)
                return;
            if (isRing) {
                const r1 = mapAngle(a);
                const r2 = mapAngle(b);
                target.arcStroke(cx, cy, arcRadius, Math.min(r1, r2), Math.max(r1, r2), false, {
                    color,
                    width: wide ? fillStrokePx : trackStrokePx,
                });
            }
            else {
                const half = wide ? fillHalf : trackHalf;
                const lo = bandCenter - half;
                const hi = bandCenter + half;
                if (isHorizontal) {
                    const xa = mapX(a);
                    const xb = mapX(b);
                    target.box(Math.round(Math.min(xa, xb)), lo, Math.round(Math.max(xa, xb)), hi, color);
                }
                else {
                    const ya = mapY(a);
                    const yb = mapY(b);
                    target.box(lo, Math.round(Math.min(ya, yb)), hi, Math.round(Math.max(ya, yb)), color);
                }
            }
        };
        // Approximate the pixel length of an interval, to choose a gradient sub-step count.
        const pixelLen = (a, b) => {
            if (isRing)
                return arcRadius * Math.abs(posToAngle(b) - posToAngle(a));
            return isHorizontal ? Math.abs(posToX(b) - posToX(a)) : Math.abs(posToY(b) - posToY(a));
        };
        // Paint an interval [a, b] of a run onto `target`, applying a color transform.
        const paintRunInterval = (target, a, b, run, transform, wide) => {
            if (b - a <= 1e-6)
                return;
            const steps = run.gradient ? Math.max(1, Math.min(64, Math.ceil(pixelLen(a, b) / 2))) : 1;
            for (let s = 0; s < steps; s++) {
                const sa = a + ((b - a) * s) / steps;
                const sb = a + ((b - a) * (s + 1)) / steps;
                paintSolid(target, sa, sb, cssOf(transform(rgbaAt(run, (sa + sb) / 2))), wide);
            }
        };
        await img.usingTemporaryLayer(element.opacity, async (layer) => {
            await layer.usingRotation(drawBounds, element.rotation, async () => {
                // Whether the ring forms a complete circle (no ends to round).
                const fullCircle = isRing && sweepDeg >= 360 && fillLo <= 1e-6 && fillHi >= 100 - 1e-6;
                const partialRing = isRing && sweepDeg < 360;
                // --- Track pass: the parts of each run NOT covered by the fill. ---
                const paintTrack = (target) => {
                    for (const run of runs) {
                        const leftHi = Math.min(run.end, hasFill ? fillLo : run.end);
                        if (leftHi > run.start)
                            paintRunInterval(target, run.start, leftHi, run, trackTransform, false);
                        if (hasFill) {
                            const rightLo = Math.max(run.start, fillHi);
                            if (run.end > rightLo)
                                paintRunInterval(target, rightLo, run.end, run, trackTransform, false);
                        }
                    }
                    // On a partial ring the open track ends follow the rounded-ends flag.
                    if (partialRing && element.roundedEnds) {
                        const r = trackStrokePx / 2;
                        const lastRun = runs[runs.length - 1];
                        const ends = [
                            [0, runs[0].colorStart],
                            [100, lastRun.gradient ? lastRun.colorEnd : lastRun.colorStart],
                        ];
                        for (const [p, colorNum] of ends) {
                            const ang = mapAngle(p);
                            target.circle(cx + arcRadius * Math.cos(ang), cy + arcRadius * Math.sin(ang), r, r, 0, Math.PI * 2, false, cssOf(trackTransform(rgbRev(colorNum, true))));
                        }
                    }
                };
                if (trackStyle === 'transparent') {
                    // Composite the whole track through one layer so the requested transparency is
                    // applied once, and anti-aliased seams between runs don't accumulate into bright lines.
                    await layer.usingTemporaryLayer(trackAmount, async (trackLayer) => paintTrack(trackLayer));
                }
                else {
                    paintTrack(layer);
                }
                // --- Fill pass: the active portion of each run (full width). ---
                if (hasFill) {
                    for (const run of runs) {
                        const aLo = Math.max(run.start, fillLo);
                        const aHi = Math.min(run.end, fillHi);
                        if (aHi <= aLo)
                            continue;
                        if (multiColour) {
                            paintRunInterval(layer, aLo, aHi, run, (c) => c, true);
                        }
                        else {
                            paintSolid(layer, aLo, aHi, parseColor(singleColor), true);
                        }
                    }
                    // Rounded ends on a ring active fill (skip when the fill is a complete circle).
                    if (isRing && element.roundedEnds && !fullCircle) {
                        const capRadius = fillStrokePx / 2;
                        const colorAtPos = (p) => {
                            if (!multiColour)
                                return singleColor;
                            const run = runs.find((r) => p >= r.start && p <= r.end) ?? runs[runs.length - 1];
                            if (!run.gradient)
                                return run.colorStart;
                            const span = run.end - run.start;
                            // Use whichever stop color the position is closer to.
                            return span > 0 && p - run.start > span / 2 ? run.colorEnd : run.colorStart;
                        };
                        for (const p of [fillLo, fillHi]) {
                            const ang = mapAngle(p);
                            layer.circle(cx + arcRadius * Math.cos(ang), cy + arcRadius * Math.sin(ang), capRadius, capRadius, 0, Math.PI * 2, false, parseColor(colorAtPos(p)));
                        }
                    }
                }
                // --- Marker pass: a single-color line at the value, spanning the full fill width. ---
                if (element.markerEnabled) {
                    const markerColor = parseColor(element.markerColor);
                    const cap = element.roundedEnds ? 'round' : 'butt';
                    // The marker follows the value: its leading edge(s). In symmetric mode that's both
                    // fill edges; otherwise the single value position.
                    const positions = symmetric ? [fillLo, fillHi] : [valuePos];
                    for (const rawP of positions) {
                        const p = Math.max(0, Math.min(100, rawP));
                        if (isRing) {
                            // A short arc bead that follows the ring's curve, full ring width, ends matching
                            // the rounded-ends flag — so it reads as a slice of the fill, not a straight line.
                            const centerAng = posToAngle(p);
                            const halfAng = Math.max(1, ringWidthPx * markerW) / 2 / arcRadius;
                            layer.arcStroke(cx, cy, arcRadius, centerAng - halfAng, centerAng + halfAng, false, {
                                color: markerColor,
                                width: ringWidthPx,
                                cap,
                            });
                        }
                        else if (isHorizontal) {
                            const mx = posToX(p);
                            layer.line(mx, bandCenter - fillHalf, mx, bandCenter + fillHalf, {
                                color: markerColor,
                                width: Math.max(1, crossFull * markerW),
                                cap,
                            });
                        }
                        else {
                            const my = posToY(p);
                            layer.line(bandCenter - fillHalf, my, bandCenter + fillHalf, my, {
                                color: markerColor,
                                width: Math.max(1, crossFull * markerW),
                                cap,
                            });
                        }
                    }
                }
            });
        });
        return drawBounds;
    }
    /**
     * Draw the selection marker for the selected element: its four bounds edges as lines that follow the
     * element's rotation and extend across the whole image (intentionally overshooting to be very visible).
     */
    static #drawBoundsLines(img, marker) {
        const lineStyle = { color: 'rgb(255, 0, 0)', width: 1 }; // TODO - what color is best?
        // Outset by half the line width so the marker sits just outside the element (its inner edge on the
        // bound) rather than straddling it and covering the element's own edge pixels.
        const outset = lineStyle.width / 2;
        for (const [x1, y1, x2, y2] of computeSelectionMarkerLines(marker, img.width, img.height, outset)) {
            img.line(x1, y1, x2, y2, lineStyle);
        }
    }
    static #angleToRadians(angle) {
        const normalizedAngle = (angle + 180) % 360;
        return (normalizedAngle / 180) * Math.PI;
    }
}
//# sourceMappingURL=LayeredRenderer.js.map